import { useLayoutEffect, useRef, type RefObject } from "react";
import { matsushimaLogoMotion as motion } from "./matsushimaLogoMotion";

type LogoElements = {
  svg: SVGSVGElement;
  root: SVGElement;
  vehicle: SVGElement;
  rearWheel: SVGElement;
  frontWheel: SVGElement;
  introCab: SVGElement | null;
  crane: SVGElement;
  cable: SVGElement;
  hook: SVGElement;
  craneEmphasis: SVGElement | null;
  wordmark: SVGElement;
  tagline: SVGElement;
};

type MotionGeometry = {
  sceneEntryPx: number;
  sceneSettlePx: number;
  movingWheelTravelPx: number;
  craneTransformOrigin: string;
  loadTransformOrigin: string;
};

type LogoAnimationStructure = {
  craneRig: SVGGElement;
  loadUpright: SVGGElement;
  loadContent: SVGGElement;
  restore: () => void;
};

type ScheduledFrame =
  | { kind: "animation-frame"; id: number }
  | { kind: "timeout"; id: number };

type LogoAnimationSession = {
  retain: () => void;
  release: () => void;
};

const SVG_NAMESPACE = "http://www.w3.org/2000/svg";
const logoAnimationSessions = new WeakMap<HTMLElement, LogoAnimationSession>();

export function useMatsushimaLogoAnimation(
  containerRef: RefObject<HTMLSpanElement | null>,
  shouldAnimate: boolean,
  onComplete?: () => void,
) {
  const onCompleteRef = useRef(onComplete);

  useLayoutEffect(() => {
    onCompleteRef.current = onComplete;
  }, [onComplete]);

  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!shouldAnimate) {
      return undefined;
    }

    if (
      !container ||
      typeof Element.prototype.animate !== "function"
    ) {
      onCompleteRef.current?.();
      return undefined;
    }

    const activeSession = logoAnimationSessions.get(container);
    if (activeSession) {
      activeSession.retain();
      return activeSession.release;
    }

    const elements = resolveLogoElements(container);
    if (!elements) {
      onCompleteRef.current?.();
      return undefined;
    }

    const session = createLogoAnimationSession(
      container,
      elements,
      () => onCompleteRef.current?.(),
    );
    logoAnimationSessions.set(container, session);
    return session.release;
  }, [containerRef, shouldAnimate]);
}

function createLogoAnimationSession(
  container: HTMLElement,
  elements: LogoElements,
  onComplete: () => void,
): LogoAnimationSession {
  const structure = prepareAnimationStructure(elements);
  const trackedElements = uniqueElements([
    elements.root,
    elements.frontWheel,
    elements.introCab,
    elements.cable,
    elements.tagline,
    elements.craneEmphasis,
    structure.craneRig,
    structure.loadUpright,
  ]);
  const originalStyles = new Map(
    trackedElements.map((element) => [
      element,
      element.getAttribute("style"),
    ]),
  );
  const originalLogoState = elements.svg.getAttribute("data-logo-state");
  const geometry = resolveMotionGeometry(elements.svg);
  const animations: Animation[] = [];
  let referenceCount = 1;
  let releaseTimer: number | null = null;
  let disposed = false;
  let scheduledFrame: ScheduledFrame | null = null;
  let cancelManualTimeline: (() => void) | null = null;
  let completed = false;
  const forceTimeline = prefersReducedMotion();

  const restoreCompleteLogo = () => {
    for (const [element, style] of originalStyles) {
      if (style === null) {
        element.removeAttribute("style");
      } else {
        element.setAttribute("style", style);
      }
    }

    if (originalLogoState === null) {
      elements.svg.setAttribute("data-logo-state", "complete");
    } else {
      elements.svg.setAttribute("data-logo-state", originalLogoState);
    }
  };

  const cancelAnimations = () => {
    for (const animation of animations) {
      animation.cancel();
    }
  };

  const complete = () => {
    if (completed || disposed) {
      return;
    }

    completed = true;
    cancelAnimations();
    restoreCompleteLogo();
    onComplete();
  };

  const dispose = () => {
    if (disposed) {
      return;
    }

    disposed = true;
    cancelScheduledFrame(scheduledFrame);
    cancelManualTimeline?.();
    cancelAnimations();
    restoreCompleteLogo();
    structure.restore();
    if (logoAnimationSessions.get(container) === session) {
      logoAnimationSessions.delete(container);
    }
  };

  const session: LogoAnimationSession = {
    retain: () => {
      referenceCount += 1;
      if (releaseTimer !== null) {
        window.clearTimeout(releaseTimer);
        releaseTimer = null;
      }
    },
    release: () => {
      referenceCount = Math.max(0, referenceCount - 1);
      if (referenceCount > 0 || releaseTimer !== null) {
        return;
      }

      releaseTimer = window.setTimeout(() => {
        releaseTimer = null;
        if (referenceCount === 0) {
          dispose();
        }
      }, 0);
    },
  };

  try {
    applyInitialState(elements, structure, geometry);
    elements.svg.setAttribute("data-logo-state", "animating");

    scheduledFrame = scheduleBeforePaint(() => {
      if (disposed) {
        restoreCompleteLogo();
        return;
      }

      try {
        startTimeline(elements, structure, geometry, animations);
        if (forceTimeline) {
          for (const animation of animations) {
            animation.pause();
            animation.currentTime = 0;
          }
          cancelManualTimeline = driveTimelineManually(
            animations,
            () => {
              elements.svg.setAttribute("data-logo-state", "animating");
            },
            () => {
              if (disposed) {
                return;
              }

              complete();
            },
          );
          return;
        }

        void Promise.allSettled(
          animations.map((animation) => animation.finished),
        ).then(() => {
          if (disposed) {
            return;
          }

          complete();
        });
      } catch {
        complete();
      }
    });
  } catch {
    complete();
  }

  return session;
}

function prepareAnimationStructure(
  elements: LogoElements,
): LogoAnimationStructure {
  const placements = [
    createPlacement(elements.crane),
    createPlacement(elements.cable),
    createPlacement(elements.hook),
    createPlacement(elements.tagline),
  ];
  const craneRig = document.createElementNS(SVG_NAMESPACE, "g");
  const loadUpright = document.createElementNS(SVG_NAMESPACE, "g");
  const loadContent = document.createElementNS(SVG_NAMESPACE, "g");

  craneRig.setAttribute("data-logo-animation-part", "crane-rig");
  loadUpright.setAttribute("data-logo-animation-part", "load-upright");
  loadContent.setAttribute("data-logo-animation-part", "load-content");

  elements.vehicle.insertBefore(craneRig, elements.crane);
  craneRig.appendChild(elements.crane);
  craneRig.appendChild(loadUpright);
  loadUpright.appendChild(elements.cable);
  loadUpright.appendChild(loadContent);
  loadContent.appendChild(elements.hook);
  loadContent.appendChild(elements.tagline);

  let restored = false;
  return {
    craneRig,
    loadUpright,
    loadContent,
    restore: () => {
      if (restored) {
        return;
      }

      restored = true;
      for (const placement of placements) {
        placement.placeholder.replaceWith(placement.element);
      }
      craneRig.remove();
    },
  };
}

function createPlacement(element: SVGElement) {
  const parent = element.parentNode;
  if (!parent) {
    throw new Error(`Cannot animate detached logo element #${element.id}`);
  }

  const placeholder = document.createComment(`restore-${element.id}`);
  parent.insertBefore(placeholder, element);
  return { element, placeholder };
}

function resolveLogoElements(container: HTMLElement): LogoElements | null {
  const byId = (id: string) =>
    container.querySelector<SVGElement>(`#${id}`);
  const svg = container.querySelector<SVGSVGElement>("svg");
  const root = byId(motion.ids.root);
  const vehicle = byId(motion.ids.vehicle);
  const rearWheel = byId(motion.ids.rearWheel);
  const frontWheel = byId(motion.ids.frontWheel);
  const introCab = byId(motion.ids.introCab);
  const crane = byId(motion.ids.crane);
  const cable = byId(motion.ids.cable);
  const hook = byId(motion.ids.hook);
  const craneEmphasis = byId(motion.ids.craneEmphasis);
  const wordmark = byId(motion.ids.wordmark);
  const tagline = byId(motion.ids.tagline);

  if (
    !svg ||
    !root ||
    !vehicle ||
    !rearWheel ||
    !frontWheel ||
    !crane ||
    !cable ||
    !hook ||
    !wordmark ||
    !tagline
  ) {
    return null;
  }

  return {
    svg,
    root,
    vehicle,
    rearWheel,
    frontWheel,
    introCab,
    crane,
    cable,
    hook,
    craneEmphasis,
    wordmark,
    tagline,
  };
}

function resolveMotionGeometry(svg: SVGSVGElement): MotionGeometry {
  const nativeViewBox = svg.viewBox?.baseVal;
  const viewBoxParts = svg
    .getAttribute("viewBox")
    ?.trim()
    .split(/[\s,]+/)
    .map(Number);
  const viewBoxWidth =
    nativeViewBox?.width && nativeViewBox.width > 0
      ? nativeViewBox.width
      : viewBoxParts?.[2] ?? 0;
  const viewBoxHeight =
    nativeViewBox?.height && nativeViewBox.height > 0
      ? nativeViewBox.height
      : viewBoxParts?.[3] ?? 0;
  const rect = svg.getBoundingClientRect();
  const scaleX =
    rect.width > 0 && viewBoxWidth > 0
      ? rect.width / viewBoxWidth
      : 1;

  return {
    sceneEntryPx: motion.distance.sceneEntrySvgUnits * scaleX,
    sceneSettlePx: motion.distance.sceneSettleSvgUnits * scaleX,
    movingWheelTravelPx:
      motion.distance.movingWheelTravelSvgUnits * scaleX,
    craneTransformOrigin: toViewBoxOrigin(
      motion.distance.cranePivot,
      viewBoxWidth,
      viewBoxHeight,
    ),
    loadTransformOrigin: toViewBoxOrigin(
      motion.distance.craneTip,
      viewBoxWidth,
      viewBoxHeight,
    ),
  };
}

function toViewBoxOrigin(
  point: { x: number; y: number },
  width: number,
  height: number,
) {
  if (width <= 0 || height <= 0) {
    return `${point.x}px ${point.y}px`;
  }

  return `${(point.x / width) * 100}% ${(point.y / height) * 100}%`;
}

function applyInitialState(
  elements: LogoElements,
  structure: LogoAnimationStructure,
  geometry: MotionGeometry,
) {
  elements.root.style.setProperty("transform-box", "view-box");
  elements.root.style.transformOrigin = "center";
  elements.root.style.transform = `translateX(${geometry.sceneEntryPx}px)`;

  elements.frontWheel.style.setProperty("transform-box", "view-box");
  elements.frontWheel.style.transform =
    `translateX(${geometry.movingWheelTravelPx}px)`;

  structure.craneRig.style.setProperty("transform-box", "view-box");
  structure.craneRig.style.transformOrigin = geometry.craneTransformOrigin;
  structure.craneRig.style.transform = "rotate(0deg)";

  structure.loadUpright.style.setProperty("transform-box", "view-box");
  structure.loadUpright.style.transformOrigin = geometry.loadTransformOrigin;
  structure.loadUpright.style.transform = "rotate(0deg)";

  // Keep the supplied final cable length throughout the entire motion.
  elements.cable.style.strokeLinecap = "butt";

  // Hide the approved tagline during entry and descent. It becomes visible only
  // after the crane reaches its lowest point, while still below the SVG crop.
  elements.tagline.style.opacity = "0";

  if (elements.introCab) {
    elements.introCab.style.opacity = "0";
  }

  if (elements.craneEmphasis) {
    elements.craneEmphasis.style.opacity = "0";
  }
}

function startTimeline(
  elements: LogoElements,
  structure: LogoAnimationStructure,
  geometry: MotionGeometry,
  animations: Animation[],
) {
  const lowerStart = timelineOffset(motion.timing.craneLowerStartMs);
  const lowerEnd = timelineOffset(motion.timing.craneLowerEndMs);
  const liftStart = timelineOffset(motion.timing.liftStartMs);
  const liftEnd = timelineOffset(motion.timing.liftEndMs);
  const loweredAngle = motion.distance.craneLowerAngleDeg;

  animate(
    elements.root,
    [
      { transform: `translateX(${geometry.sceneEntryPx}px)` },
      { offset: 0.94, transform: "translateX(0)" },
      {
        offset: 0.985,
        transform: `translateX(${geometry.sceneSettlePx}px)`,
      },
      { transform: "translateX(0)" },
    ],
    motion.timing.sceneEntry,
    motion.easing.sceneEntry,
    animations,
  );

  animate(
    structure.craneRig,
    [
      { offset: 0, transform: "rotate(0deg)" },
      {
        offset: lowerStart,
        transform: "rotate(0deg)",
        easing: motion.easing.craneLower,
      },
      { offset: lowerEnd, transform: `rotate(${loweredAngle}deg)` },
      {
        offset: liftStart,
        transform: `rotate(${loweredAngle}deg)`,
        easing: motion.easing.lift,
      },
      { offset: liftEnd, transform: "rotate(0deg)" },
      { offset: 1, transform: "rotate(0deg)" },
    ],
    { delay: 0, duration: motion.totalDurationMs },
    "linear",
    animations,
  );

  animate(
    structure.loadUpright,
    [
      { offset: 0, transform: "rotate(0deg)" },
      {
        offset: lowerStart,
        transform: "rotate(0deg)",
        easing: motion.easing.craneLower,
      },
      { offset: lowerEnd, transform: `rotate(${-loweredAngle}deg)` },
      {
        offset: liftStart,
        transform: `rotate(${-loweredAngle}deg)`,
        easing: motion.easing.lift,
      },
      { offset: liftEnd, transform: "rotate(0deg)" },
      { offset: 1, transform: "rotate(0deg)" },
    ],
    { delay: 0, duration: motion.totalDurationMs },
    "linear",
    animations,
  );

  animate(
    elements.frontWheel,
    [
      {
        offset: 0,
        transform: `translateX(${geometry.movingWheelTravelPx}px)`,
      },
      {
        offset: liftStart,
        transform: `translateX(${geometry.movingWheelTravelPx}px)`,
        easing: motion.easing.lift,
      },
      { offset: liftEnd, transform: "translateX(0)" },
      { offset: 1, transform: "translateX(0)" },
    ],
    { delay: 0, duration: motion.totalDurationMs },
    "linear",
    animations,
  );

  animate(
    elements.tagline,
    [{ opacity: 0 }, { opacity: 1 }],
    { delay: motion.timing.liftStartMs, duration: 1 },
    "steps(1, jump-start)",
    animations,
  );
}

function timelineOffset(timeMs: number) {
  return timeMs / motion.totalDurationMs;
}

function animate(
  element: SVGElement,
  keyframes: Keyframe[],
  timing: { delay: number; duration: number },
  easing: string,
  animations: Animation[],
  fill: FillMode = "both",
) {
  animations.push(
    element.animate(keyframes, {
      delay: timing.delay,
      duration: timing.duration,
      easing,
      fill,
    }),
  );
}

function driveTimelineManually(
  animations: Animation[],
  onFrame: () => void,
  onComplete: () => void,
) {
  let frame: ScheduledFrame | null = null;
  let startedAt: number | null = null;
  let cancelled = false;

  const tick: FrameRequestCallback = (timestamp) => {
    if (cancelled) {
      return;
    }

    startedAt ??= timestamp;
    onFrame();
    const elapsed = Math.min(
      timestamp - startedAt,
      motion.totalDurationMs,
    );
    for (const animation of animations) {
      animation.currentTime = elapsed;
    }

    if (elapsed >= motion.totalDurationMs) {
      onComplete();
      return;
    }

    frame = scheduleBeforePaint(tick);
  };

  frame = scheduleBeforePaint(tick);

  return () => {
    cancelled = true;
    cancelScheduledFrame(frame);
  };
}

function prefersReducedMotion() {
  return (
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
}

function uniqueElements(elements: Array<SVGElement | null>) {
  return Array.from(
    new Set(elements.filter((element): element is SVGElement => element !== null)),
  );
}

function scheduleBeforePaint(callback: FrameRequestCallback): ScheduledFrame {
  if (typeof window.requestAnimationFrame === "function") {
    return {
      kind: "animation-frame",
      id: window.requestAnimationFrame(callback),
    };
  }

  return {
    kind: "timeout",
    id: window.setTimeout(() => callback(performance.now()), 0),
  };
}

function cancelScheduledFrame(frame: ScheduledFrame | null) {
  if (!frame) {
    return;
  }

  if (frame.kind === "animation-frame") {
    window.cancelAnimationFrame(frame.id);
  } else {
    window.clearTimeout(frame.id);
  }
}
